import { Paper, Typography } from '@material-ui/core';
import type { CockpitRelation } from '@internal/spassu-code-contract';
import type {
  CockpitArtifactView,
  CockpitSectionViewState,
  CockpitSkillView,
  CockpitViewNode,
} from '../model/cockpitViewModel';
import { resolveEvidenceContextNotice } from '../model/cockpitPresentationPolicy';
import { relationKindLabel } from '../model/semanticLabels';
import { CockpitSectionHead } from './CockpitSectionHead';
import { ContextualNotice } from './ContextualNotice';
import { EvidenceLane } from './EvidenceLane';
import { useCockpitStyles } from './cockpitStyles';

export interface EvidenceFlowProps {
  showArtifactLane?: boolean;
  skills: CockpitSkillView[];
  artifacts: CockpitArtifactView[];
  relations: CockpitRelation[];
  sectionState: CockpitSectionViewState;
  selectedId: string | null;
  selectedKind: CockpitViewNode['kind'] | null;
  relatedIds: string[];
  onSelect: (kind: CockpitViewNode['kind'], id: string) => void;
}

export const EvidenceFlow = (props: EvidenceFlowProps) => {
  const { showArtifactLane = true } = props;
  const classes = useCockpitStyles();
  const summary = summarizeRelations(props.relations, props.selectedId);
  const notice = resolveEvidenceContextNotice(
    props.sectionState,
    summary.total,
  );
  return (
    <Paper
      component="section"
      className={classes.panel}
      aria-labelledby="evidence-flow-title"
    >
      <CockpitSectionHead id="evidence-flow-title" title="Fluxo de evidências">
        O que cada skill produziu e com que confiança isso foi observado.
        Selecione um item para ver suas relações.
      </CockpitSectionHead>
      {notice && (
        <ContextualNotice
          label="Aviso sobre relações do fluxo"
          notice={notice}
        />
      )}
      <div className={classes.lanes}>
        <EvidenceLane title="Skills" nodes={props.skills} {...props} />
        {showArtifactLane && (
          <EvidenceLane
            title="Artefatos no fluxo"
            nodes={props.artifacts}
            {...props}
          />
        )}
      </div>
      <SelectedRelations
        relations={props.relations}
        selectedId={props.selectedId}
      />
    </Paper>
  );
};

function SelectedRelations({
  relations,
  selectedId,
}: {
  relations: CockpitRelation[];
  selectedId: string | null;
}) {
  if (!selectedId) return null;
  const selectedRelations = relations.filter(
    relation =>
      relation.from.id === selectedId || relation.to.id === selectedId,
  );
  return (
    <section aria-label="Relações da seleção">
      <Typography variant="subtitle2">
        Relações observadas da seleção
      </Typography>
      {selectedRelations.length === 0 ? (
        <Typography>Nenhuma relação observada para este item.</Typography>
      ) : (
        <ul>
          {selectedRelations.map(relation => (
            <li key={relation.id}>
              {relation.from.id} — {relationKindLabel(relation.kind)} —{' '}
              {relation.to.id}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function summarizeRelations(
  relations: CockpitRelation[],
  selectedId: string | null,
) {
  return {
    total: relations.length,
    critical: relations.filter(
      relation =>
        relation.kind === 'validated-by' || relation.kind === 'delivered-by',
    ).length,
    selected: selectedId
      ? relations.filter(
          relation =>
            relation.from.id === selectedId || relation.to.id === selectedId,
        ).length
      : 0,
  };
}
